import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { usePressEscape, useProjectAction, useRouteParams } from 'hooks';

import {
    SchedulerChildParentMode,
    SchedulerProjectParentMode,
    SchedulerSingleModeContextMenu,
} from '@hub-mono/app-modules';
import { PARENT } from '../../../enums/viewModeEnum';
import { useDispatch, useSelector } from 'react-redux';
import { companyApprovers } from '../../../../../actions/companyActions';
import { getCategoryTemplates } from '../../../../../actions/categoryTemplateActions';
import { getCategoryGroups } from '../../../../../actions/categoryGroupActions';
import { useBooking, useGetViewObject, usePhases } from '../../../hooks';
import { useMilestones } from '../../../hooks/useMilestones';
import { useProjectDates } from '../../../hooks/useProjectDates';
import { initContextMenuScheduleForm } from '../../../../../actions/schedulerActions';
import { selectCompanyApprovers } from '../../../../../selectors/company';
import { selectMappedResourceGroups } from '../../../../../selectors/resourceGroup';
import { getResourcesMap } from '../../../../../selectors/resource';
import { getFormDefaultApprovers } from '../../../../request/utils/getFormDefaultApprovers';

const Schedule = ({ top, left, schedulerRef, close, viewData }) => {
    usePressEscape(close);

    return (
        <NewScheduleMenu
            onClose={close}
            position={{ top, left }}
            isOpen={true}
            schedulerRef={schedulerRef}
            viewData={viewData}
        />
    );
};

Schedule.propTypes = {
    schedulerRef: PropTypes.object.isRequired,
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired,
    close: PropTypes.func.isRequired,
};

export default Schedule;

export const NewScheduleMenu = ({
    onClose,
    schedulerRef,
    position,
    isOpen,
    anchorEl,
    scheduleToolBarContext,
    requestToolBarContext,
    viewData,
}) => {
    const dispatch = useDispatch();

    const { createProject } = useProjectAction(schedulerRef);
    const { pasteBooking, requestProject, createBooking, updateBooking } = useBooking(null, onClose);
    const { createMilestone } = useMilestones(schedulerRef, onClose);
    const { createPhase } = usePhases(schedulerRef, onClose);
    const { updateProjectDates } = useProjectDates(schedulerRef, onClose);
    const viewObject = useGetViewObject();
    const params = useRouteParams();
    const approvers = useSelector(selectCompanyApprovers);
    const mappedResourceGroups = useSelector(selectMappedResourceGroups);
    const resourcesByIds = useSelector(getResourcesMap);

    const handleRequest = useCallback(
        data => {
            const resource = resourcesByIds[data.resource];

            let defaultApprovers;

            if (resource) {
                defaultApprovers = getFormDefaultApprovers({ approvers, resource, mappedResourceGroups });
            }

            if (defaultApprovers) {
                createBooking({
                    ...data,
                    approvalInfo: {
                        approvers: defaultApprovers.map(approver => ({ id: approver._id })),
                    },
                });
            } else {
                createBooking(data);
            }
        },
        [approvers, createBooking, mappedResourceGroups, resourcesByIds]
    );

    const handleRequestVacation = useCallback(
        data => {
            const resource = resourcesByIds[data.resource];

            let defaultApprovers;

            if (resource) {
                defaultApprovers = getFormDefaultApprovers({ approvers, resource, mappedResourceGroups });
            }

            if (defaultApprovers) {
                requestProject({
                    ...data,
                    approvalInfo: {
                        approvers: defaultApprovers.map(approver => ({ id: approver._id })),
                    },
                });
            } else {
                requestProject(data);
            }
        },
        [approvers, mappedResourceGroups, requestProject, resourcesByIds]
    );

    useEffect(() => {
        dispatch(companyApprovers.request());
        dispatch(getCategoryTemplates.request());
        dispatch(getCategoryGroups.request());
        dispatch(
            initContextMenuScheduleForm.request({
                edit: false,
                resourceId: params.resourceId,
                projectId: params.projectId,
            })
        );
    }, [dispatch, params.projectId, params.resourceId]);

    const isToolbar = false;
    const currentSelection = useSelector(state => state.scheduler.currentSelection);
    const { rowTags } = currentSelection;
    const mode = schedulerRef.current?.control.mode;
    const showChildrenContextMenu =
        rowTags &&
        !isToolbar &&
        !rowTags.parent &&
        mode === PARENT.value &&
        (rowTags.isProjectRow || rowTags.isResourceRow);
    let condition2 = showChildrenContextMenu;
    let condition3 = rowTags && rowTags.parent && rowTags.isProjectRow && !isToolbar;

    if (condition3) {
        return (
            <SchedulerProjectParentMode
                viewData={viewData}
                viewObject={viewObject}
                onClose={onClose}
                position={position}
                isOpen={isOpen}
                anchorEl={anchorEl}
                onCreateMilestone={createMilestone}
                onCreatePhase={createPhase}
                onUpdateProjectDates={updateProjectDates}
            />
        );
    }

    if (condition2) {
        return (
            <SchedulerChildParentMode
                viewData={viewData}
                viewObject={viewObject}
                onClose={onClose}
                position={position}
                isOpen={isOpen}
                anchorEl={anchorEl}
                onPasteBooking={pasteBooking}
                onSchedule={createBooking}
                onRequest={handleRequest}
                onRequestVacation={handleRequestVacation}
            />
        );
    }

    return (
        <SchedulerSingleModeContextMenu
            viewData={viewData}
            viewObject={viewObject}
            scheduleToolBarContext={scheduleToolBarContext}
            requestToolBarContext={requestToolBarContext}
            isOpen={isOpen}
            anchorEl={anchorEl}
            position={position}
            onPasteBooking={pasteBooking}
            onRequestVacation={handleRequestVacation}
            onClose={onClose}
            onCreateMilestone={createMilestone}
            onCreatePhase={createPhase}
            onUpdateProjectDates={updateProjectDates}
            onSchedule={createBooking}
            onRequest={handleRequest}
            onUpdateBooking={updateBooking}
            onCreateProject={createProject}
        />
    );
};
